import { MethodTypeKind } from '@protocols/domain';
import { AssetType, AssetTypeRepository, MethodTypeRepository } from '@telespot/domain';

export interface ListMethodTypesInput {
  searchTerm?: string;
  limit?: number;
  lastItemName?: string;
}

export interface ListMethodTypesOutput {
  id: string;
  name: string;
  type?: MethodTypeKind;
}

export interface IListMethodTypesQuery {
  /**
   * Lists method types ordered by name, paginated by `lastItemName` cursor.
   * Each item is enriched with the kind ('image' | 'video' | 'audio' | 'text')
   * of its referenced asset type. The kind is `undefined` when the method type
   * has no asset type, the asset type cannot be resolved, or its kind is unset.
   * A method type outlives the asset type it points at, so a dangling pointer
   * (or a soft-deleted asset type) simply lists without a kind — the repository
   * answers `undefined` for those. Real repository errors propagate so the
   * caller's pagination effect can surface them.
   */
  execute(input: ListMethodTypesInput): Promise<ListMethodTypesOutput[]>;
}

export class ListMethodTypesQuery implements IListMethodTypesQuery {
  constructor(
    private readonly methodTypeRepository: MethodTypeRepository,
    private readonly assetTypeRepository: AssetTypeRepository
  ) {}

  public async execute(input: ListMethodTypesInput): Promise<ListMethodTypesOutput[]> {
    const methodTypes = await this.methodTypeRepository.getAll(
      input.searchTerm,
      input.limit,
      input.lastItemName
    );

    const distinctAssetTypeIds = Array.from(
      new Set(methodTypes.map((mt) => mt.assetTypeId).filter((id): id is string => !!id))
    );
    const resolvedAssetTypes = await Promise.all(
      distinctAssetTypeIds.map((id) => this.assetTypeRepository.getById(id))
    );
    const assetTypesById = new Map<string, AssetType>();
    distinctAssetTypeIds.forEach((id, i) => {
      const assetType = resolvedAssetTypes[i];
      if (assetType) {
        assetTypesById.set(id, assetType);
      }
    });

    return methodTypes.map((mt) => ({
      id: mt.id,
      name: mt.name,
      type: mt.assetTypeId ? assetTypesById.get(mt.assetTypeId)?.type : undefined,
    }));
  }
}
